import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Calendar,
  ErrorState,
  Icon,
  Input,
  Modal,
  Select,
  Settings,
  SurfaceCard,
} from '@react-apps/design-system';
import type { HarrisonPromptVersion } from '../../domain/entities/harrison.entity';
import {
  useCreateHarrisonPrompt,
  useHarrisonDraft,
  useHarrisonModels,
  useHarrisonPrompts,
  useMakeHarrisonPromptDefault,
} from '../hooks/use-harrison';
import {
  formatPromptDate,
  harrisonErrorMessage,
  modelLabel,
  orderPrompts,
  promptLabel,
  promptPreview,
  promptToHtml,
  selectableModels,
} from '../harrison';
import { richTextIsEmpty } from '@react-apps/content-editor';
import { notifyDone, notifyFailed } from '../display';
import { ConfirmModal } from './ConfirmModal';
import { RichTextEditor } from './RichTextEditor';

/**
 * O Harrison para comentário oficial (DESEN-34), num componente só que
 * qualquer tela usa passando a questão: o seletor de modelo, o botão que pede
 * o comentário e o "Configurar prompt", com o modal do histórico e do
 * formulário de prompt.
 *
 * **O que ele devolve:** `onGenerated(markdown, model)` com a resposta do
 * gerar **como o back a manda** — markdown, sem conversão nem sanitização.
 * Exibir, converter e aplicar é de quem integra. Para pôr num editor de texto
 * rico do content, `markdownToEditorHtml(markdown)` (`../harrison`) converte e
 * sanitiza com o allowlist do back; para gravar, a origem do comentário sai de
 * `commentOrigin` (`../harrison`).
 *
 * ```tsx
 * <HarrisonCommentAssistant
 *   questionId={questionId}
 *   onGenerated={(markdown) => aplicarNoEditor(markdownToEditorHtml(markdown))}
 *   onGeneratingChange={setTravado}
 * />
 * ```
 *
 * - Os modelos são os do Harrison (`GET /aristo/harrison/models`, pelo back),
 *   filtrados para Terra e Luna (`selectableModels`), com o padrão dele
 *   pré-selecionado quando é um dos dois — senão, o primeiro da lista. Sem
 *   nenhum, o seletor some e a geração usa o padrão do Harrison.
 * - Desmontar aborta a geração. Falha fica num aviso `role="alert"` dentro do
 *   componente, e `onGenerated` não é chamado.
 * - Disposição da tela de referência (título; modelo e gerar à esquerda;
 *   "Configurar prompt" à direita), com o visual do admin: `Select` e `Button`
 *   do design-system e cores só de tema, para acompanhar o modo claro e o
 *   escuro.
 * - No modal: a versão em uso é sempre a primeira do histórico; o prompt em
 *   criação só existe aqui até ser salvo; trocar o padrão pede confirmação,
 *   porque vale para todo mundo, inclusive no primum-admin; 403 fecha o modal.
 */
export function HarrisonCommentAssistant({
  questionId,
  onGenerated,
  onGeneratingChange,
  disabled = false,
}: {
  /** A questão do comentário; o rascunho é pedido para ela. */
  questionId: string;
  /** A resposta do gerar, em markdown, e o modelo usado. */
  onGenerated: (markdown: string, model: string | undefined) => void;
  /** Avisa a tela para travar o que ela quiser enquanto o Harrison escreve. */
  onGeneratingChange?: (generating: boolean) => void;
  disabled?: boolean;
}) {
  const models = useHarrisonModels();
  const draft = useHarrisonDraft(questionId);
  const [model, setModel] = useState<string | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const onGeneratingChangeRef = useRef(onGeneratingChange);
  onGeneratingChangeRef.current = onGeneratingChange;

  useEffect(() => {
    onGeneratingChangeRef.current?.(draft.generating);
  }, [draft.generating]);

  // Resposta sem lista (ou falha) = sem seletor; a geração usa o padrão.
  const modelList = selectableModels(
    Array.isArray(models.data?.models) ? models.data.models : [],
  );
  const defaultModel = models.data?.defaultModel;
  const chosenModel =
    model ??
    (defaultModel && modelList.includes(defaultModel)
      ? defaultModel
      : modelList[0]);

  const generate = async () => {
    const used = chosenModel || undefined;
    const markdown = await draft.generate(used);
    if (markdown !== null) onGenerated(markdown, used);
  };

  return (
    <SurfaceCard
      role="region"
      aria-label="Harrison"
      padding="sm"
      className="space-y-3"
    >
      <h3 className="text-(--color-text-primary) text-sm font-semibold">
        Harrison
      </h3>

      {/* Disposição da tela de referência: modelo e gerar à esquerda,
          "Configurar prompt" encostado à direita. */}
      <div className="flex flex-wrap items-end gap-2">
        {modelList.length > 0 && (
          <div className="w-52">
            <Select
              label="Modelo de I.A."
              value={chosenModel ?? ''}
              disabled={draft.generating || disabled}
              onChange={(e) => setModel(e.target.value)}
              options={modelList.map((m) => ({
                value: m,
                label: modelLabel(m),
              }))}
            />
          </div>
        )}
        {/* A ação principal: `primary`, o destaque do design-system. */}
        <Button
          type="button"
          disabled={draft.generating || disabled}
          aria-busy={draft.generating}
          onClick={() => void generate()}
          className="gap-1.5"
        >
          <BrainIcon />
          {draft.generating ? 'Gerando…' : 'Comentário Harrison'}
        </Button>
        {/* `ghost`: secundário de propósito, para o gerar ser o destaque. */}
        <Button
          type="button"
          variant="ghost"
          disabled={draft.generating}
          onClick={() => setPromptOpen(true)}
          className="ml-auto gap-1.5"
        >
          <Settings size="sm" />
          Configurar prompt
        </Button>
      </div>

      {draft.generating && (
        <p className="text-(--color-text-secondary) text-xs" role="status">
          O Harrison está escrevendo. Pode levar até um minuto.
        </p>
      )}
      {draft.error != null && (
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {harrisonErrorMessage(draft.error, 'gerar')}
        </p>
      )}

      <PromptSettingsModal
        open={promptOpen}
        onClose={() => setPromptOpen(false)}
      />
    </SurfaceCard>
  );
}

/**
 * Cérebro, do botão de gerar. O design-system não tem um, e a primitiva `Icon`
 * existe para isto: mesmo traço e escala dos outros ícones.
 */
function BrainIcon() {
  return (
    <Icon size="sm">
      <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
      <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
      <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
      <path d="M12 18V5" />
    </Icon>
  );
}

// --- Configurar prompt --------------------------------------------------------

type Mode =
  | { kind: 'view' }
  | { kind: 'create'; initialHtml: string; editorKey: number };

type Pending =
  | { kind: 'saveDefault' }
  | { kind: 'makeDefault'; version: HarrisonPromptVersion }
  | { kind: 'discard'; then: () => void };

function PromptSettingsModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const prompts = useHarrisonPrompts(open);
  const create = useCreateHarrisonPrompt();
  const makeDefault = useMakeHarrisonPromptDefault();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'view' });
  const [title, setTitle] = useState('');
  const [html, setHtml] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);

  // Abrir de novo começa do zero: o que ficou da última vez não é retomado.
  useEffect(() => {
    if (!open) return;
    setMode({ kind: 'view' });
    setSelectedId(null);
    setActionError(null);
  }, [open]);

  /** 403: sem acesso ao prompt. Não há o que tentar de novo aqui: fecha. */
  const closeForbidden = (err: unknown) => {
    notifyFailed(err, 'Você não tem acesso ao prompt do Harrison');
    onClose();
  };

  useEffect(() => {
    if (open && prompts.isError && isForbidden(prompts.error)) {
      closeForbidden(prompts.error);
    }
    // `closeForbidden` muda a cada render; o gatilho é o erro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prompts.isError, prompts.error]);

  const versions = orderPrompts(prompts.data ?? []);
  // Sem escolha ainda, mostra a em uso (que é a primeira).
  const selected =
    versions.find((v) => v.id === selectedId) ?? versions[0] ?? null;
  const creating = mode.kind === 'create';
  const contentEmpty = richTextIsEmpty(html);
  const dirty = creating && (title.trim() !== '' || !contentEmpty);
  const canSave = creating && title.trim() !== '' && !contentEmpty;
  const busy = create.isPending || makeDefault.isPending;

  /** Roda `then` direto, ou depois de confirmar o descarte do que está escrito. */
  const leaveCreation = (then: () => void) => {
    if (dirty) setPending({ kind: 'discard', then });
    else then();
  };

  const startCreation = (base?: HarrisonPromptVersion) =>
    leaveCreation(() => {
      const initialHtml = base ? promptToHtml(base.content) : '';
      setTitle('');
      setHtml(initialHtml);
      setActionError(null);
      setMode({ kind: 'create', initialHtml, editorKey: Date.now() });
    });

  const selectVersion = (id: string) =>
    leaveCreation(() => {
      setMode({ kind: 'view' });
      setActionError(null);
      setSelectedId(id);
    });

  const save = async (asDefault: boolean) => {
    setActionError(null);
    try {
      const created = await create.mutateAsync({
        title: title.trim(),
        content: html,
        makeDefault: asDefault,
      });
      notifyDone(
        asDefault
          ? 'Prompt salvo e definido como padrão.'
          : 'Prompt salvo. O padrão continua o mesmo.',
      );
      setMode({ kind: 'view' });
      // O histórico relê pela invalidação; escolhe a versão recém-criada.
      const list = await prompts.refetch();
      setSelectedId(
        list.data?.find((v) => v.version === created.version)?.id ?? null,
      );
    } catch (err) {
      if (isForbidden(err)) return closeForbidden(err);
      setActionError(harrisonErrorMessage(err, 'salvar'));
      // Com o erro original, o aviso substitui o da rede global (mesmo id).
      notifyFailed(err, 'O prompt não foi salvo');
    }
  };

  const setAsDefault = async (version: HarrisonPromptVersion) => {
    setActionError(null);
    try {
      await makeDefault.mutateAsync(version.id);
      notifyDone(`"${promptLabel(version)}" agora é o prompt padrão.`);
      setSelectedId(version.id);
    } catch (err) {
      if (isForbidden(err)) return closeForbidden(err);
      setActionError(harrisonErrorMessage(err, 'tornar padrão'));
      notifyFailed(err, 'O padrão não foi trocado');
    }
  };

  const close = () => {
    if (busy) return;
    leaveCreation(onClose);
  };

  const confirmPending = () => {
    const p = pending;
    setPending(null);
    if (!p) return;
    if (p.kind === 'discard') p.then();
    if (p.kind === 'saveDefault') void save(true);
    if (p.kind === 'makeDefault') void setAsDefault(p.version);
  };

  const footer = creating ? (
    <div className="flex flex-wrap justify-end gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={() =>
          leaveCreation(() => {
            setMode({ kind: 'view' });
            setActionError(null);
          })
        }
      >
        Cancelar
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={!canSave || busy}
        onClick={() => void save(false)}
      >
        {create.isPending && create.variables?.makeDefault === false
          ? 'Salvando…'
          : 'Salvar'}
      </Button>
      <Button
        type="button"
        size="sm"
        disabled={!canSave || busy}
        onClick={() => setPending({ kind: 'saveDefault' })}
      >
        {create.isPending && create.variables?.makeDefault
          ? 'Salvando…'
          : 'Salvar e tornar padrão'}
      </Button>
    </div>
  ) : (
    <div className="flex justify-end">
      <Button type="button" variant="ghost" size="sm" onClick={close}>
        Fechar
      </Button>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title="Configurar prompt"
        description="O prompt padrão é o que o Harrison usa nas próximas gerações de comentário, para todos."
        size="full"
        closeOnBackdrop={!dirty}
        footer={footer}
      >
        <div className="flex h-full min-h-0 flex-col gap-4 md:flex-row">
          <section
            aria-label="Histórico de prompts"
            className="border-(--color-border) max-h-[35vh] shrink-0 overflow-y-auto md:max-h-none md:w-72 md:border-r md:pr-3"
          >
            <h3 className="text-(--color-text-secondary) mb-2 text-sm font-medium">
              Histórico
            </h3>
            {prompts.isLoading && (
              <p className="text-(--color-text-secondary) text-sm">
                Carregando o histórico…
              </p>
            )}
            {prompts.isError && !isForbidden(prompts.error) && (
              <ErrorState
                message={harrisonErrorMessage(prompts.error, 'carregar')}
                onRetry={() => void prompts.refetch()}
              />
            )}
            {prompts.isSuccess && versions.length === 0 && (
              <p className="text-(--color-text-secondary) text-sm">
                Nenhuma versão cadastrada. O Harrison usa o prompt interno dele
                até alguém salvar uma como padrão.
              </p>
            )}
            <ul className="space-y-2">
              {versions.map((version) => (
                <li key={version.id}>
                  <HistoryItem
                    version={version}
                    selected={!creating && version.id === selected?.id}
                    onSelect={() => selectVersion(version.id)}
                  />
                </li>
              ))}
            </ul>
          </section>

          <section
            aria-label={creating ? 'Novo prompt' : 'Prompt selecionado'}
            className="min-h-0 min-w-0 flex-1 overflow-y-auto"
          >
            {actionError && (
              <p
                className="mb-3 rounded-md border border-red-300 bg-red-50 p-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400"
                role="alert"
              >
                {actionError}
              </p>
            )}

            {creating ? (
              <div className="space-y-3">
                <Input
                  label="Título"
                  value={title}
                  maxLength={200}
                  autoFocus
                  onChange={(e) => setTitle(e.target.value)}
                />
                <RichTextEditor
                  key={mode.editorKey}
                  label="Conteúdo do prompt"
                  value={mode.initialHtml}
                  onChange={setHtml}
                />
              </div>
            ) : (
              <>
                <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-(--color-text-primary) font-semibold">
                      {selected ? promptLabel(selected) : 'Prompts'}
                    </p>
                    {selected && (
                      <p className="text-(--color-text-secondary) text-xs">
                        Versão {selected.version} ·{' '}
                        {formatPromptDate(selected.createdAt)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selected && !selected.isDefault && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          setPending({ kind: 'makeDefault', version: selected })
                        }
                      >
                        {makeDefault.isPending ? 'Trocando…' : 'Tornar padrão'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={busy || prompts.isLoading}
                      onClick={() => startCreation()}
                    >
                      Criar novo
                    </Button>
                    {selected && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busy}
                        onClick={() => startCreation(selected)}
                      >
                        Criar baseado neste
                      </Button>
                    )}
                  </div>
                </div>
                {selected && (
                  <SurfaceCard
                    padding="sm"
                    className="rich-text text-(--color-text-primary) text-sm"
                    // `promptToHtml` passa pelo `sanitizeRichText`: o HTML vem
                    // do Harrison e não é confiável.
                    dangerouslySetInnerHTML={{
                      __html: promptToHtml(selected.content),
                    }}
                  />
                )}
              </>
            )}
          </section>
        </div>
      </Modal>

      <ConfirmModal
        open={pending !== null}
        onClose={() => setPending(null)}
        onConfirm={confirmPending}
        title={
          pending?.kind === 'discard'
            ? 'Descartar o prompt em criação?'
            : 'Tornar este prompt o padrão?'
        }
        description={
          pending?.kind === 'discard'
            ? 'O texto ainda não foi salvo e será perdido.'
            : 'Ele passa a ser usado nas próximas gerações de comentário de todos os usuários, inclusive no primum-admin.'
        }
        confirmLabel={
          pending?.kind === 'discard'
            ? 'Descartar'
            : pending?.kind === 'saveDefault'
              ? 'Salvar e tornar padrão'
              : 'Tornar padrão'
        }
        danger={pending?.kind === 'discard'}
      />
    </>
  );
}

/**
 * Um card do histórico: o título em negrito com o selo "Em uso"/"Anterior", o
 * começo do prompt em duas linhas cortadas com "…" (`line-clamp-2`) e a data.
 * O prompt inteiro só aparece ao lado, na versão selecionada.
 */
function HistoryItem({
  version,
  selected,
  onSelect,
}: {
  version: HarrisonPromptVersion;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <SurfaceCard
      padding="none"
      // Anel, e não cor de borda: a borda é do `SurfaceCard`, e duas classes de
      // cor de borda disputariam pela ordem do CSS gerado.
      className={selected ? 'ring-(--color-accent) ring-2' : ''}
    >
      <button
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        className="hover:bg-(--color-bg-secondary) w-full rounded-xl p-3 text-left transition-colors"
      >
        <span className="flex items-center justify-between gap-2">
          <span className="text-(--color-text-primary) truncate text-sm font-semibold">
            {promptLabel(version)}
          </span>
          <Badge
            variant={version.isDefault ? 'success' : 'default'}
            className="shrink-0"
          >
            {version.isDefault ? 'Em uso' : 'Anterior'}
          </Badge>
        </span>
        {/* Sem `block` junto: ele também mexe no `display` e vem depois do
            `line-clamp-2` no CSS gerado, então desligava o corte. */}
        <span
          data-testid="prompt-preview"
          className="text-(--color-text-secondary) mt-1 line-clamp-2 text-xs"
        >
          {promptPreview(version.content)}
        </span>
        <span className="text-(--color-text-secondary) mt-1 flex items-center justify-end gap-1 text-[11px]">
          <Calendar size="xs" aria-hidden />
          {formatPromptDate(version.createdAt)}
        </span>
      </button>
    </SurfaceCard>
  );
}

function isForbidden(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 403;
}
